import {
  getResetablePinComponentSettings,
  getResetablePinSettings,
  ThemeSettingsSelection,
} from '@lessonup/pin-renderer';
import { Pin, PinComponent, PinComponentType } from '@lessonup/pins-shared';
import { TFunction } from 'i18next';
import { cloneDeep, isUndefined, uniq } from 'lodash';
import { ComponentTypeGroup, ComponentTypeGroupWithCount } from './types';

function hasSettingValue(settings: Pin['settings'] | PinComponent['settings'], key: string): boolean {
  return !isUndefined(settings[key as keyof typeof settings]);
}

function pinHasCustomPinSettings(pin: Pin): boolean {
  const resetableKeys = getResetablePinSettings(pin);
  return resetableKeys.some((key) => hasSettingValue(pin.settings, key));
}

function pinComponentsOfTypeHaveCustomSettings(pin: Pin, type: PinComponentType): boolean {
  return pin.pinComponents.some(
    (component) =>
      component.type === type &&
      getResetablePinComponentSettings(component).some((key) => hasSettingValue(component.settings, key))
  );
}

export function getComponentTypeGroups(pin: Pin, t: TFunction): ComponentTypeGroup[] {
  const componentTypes = uniq(pin.pinComponents.map((pc) => pc.type));

  return componentTypes
    .map((type): ComponentTypeGroup | null => {
      const firstComponent = pin.pinComponents.find((pc) => pc.type === type);
      if (!firstComponent) return null;

      const hasCustomSettings = pinComponentsOfTypeHaveCustomSettings(pin, type);
      if (!hasCustomSettings) return null;

      return {
        type,
        label: t(`contextSidebar.title.${type}`),
        settingsKeys: getResetablePinComponentSettings(firstComponent),
      };
    })
    .filter((group): group is ComponentTypeGroup => group !== null);
}

function deleteSettingKeys(settings: Pin['settings'] | PinComponent['settings'], keys: readonly string[]): void {
  keys.forEach((key) => {
    delete settings[key as keyof typeof settings];
  });
}

export function getSelectivelyStrippedPin(pin: Pin, selections: ThemeSettingsSelection): Pin {
  const pinClone = cloneDeep(pin);

  if (selections.pinSettings.background) {
    const pinSettingsKeys = getResetablePinSettings(pinClone);
    deleteSettingKeys(pinClone.settings, pinSettingsKeys);
  }

  pinClone.pinComponents.forEach((pinComponent) => {
    const shouldStripComponent = selections.componentSettings[pinComponent.type];

    if (shouldStripComponent) {
      const pinComponentSettingsKeys = getResetablePinComponentSettings(pinComponent);
      deleteSettingKeys(pinComponent.settings, pinComponentSettingsKeys);
    }
  });

  return pinClone;
}

export function getInitialThemeSettingsSelection(
  currentPin: Pin,
  allPinsToCheck: Pin[] = [currentPin]
): ThemeSettingsSelection {
  const allComponentTypes = uniq(allPinsToCheck.flatMap((pin) => pin.pinComponents.map((pc) => pc.type)));

  const componentSettings = allComponentTypes.reduce(
    (acc, type) => {
      const hasCustomSettings = allPinsToCheck.some((pin) => pinComponentsOfTypeHaveCustomSettings(pin, type));
      if (hasCustomSettings) {
        acc[type] = true;
      }
      return acc;
    },
    {} as Partial<Record<PinComponentType, boolean>>
  );

  const hasAnyPinWithCustomSettings = allPinsToCheck.some((pin) => pinHasCustomPinSettings(pin));

  return {
    pinSettings: {
      background: hasAnyPinWithCustomSettings,
    },
    componentSettings,
  };
}

export function getAggregatedComponentTypeGroups(
  pins: Pin[],
  t: TFunction,
  includeCount = false
): (ComponentTypeGroup | ComponentTypeGroupWithCount)[] {
  const allComponentTypes = uniq(pins.flatMap((pin) => pin.pinComponents.map((pc) => pc.type)));

  return allComponentTypes
    .map((type): ComponentTypeGroup | ComponentTypeGroupWithCount | null => {
      const pinsWithCustomSettings = pins.filter((pin) => pinComponentsOfTypeHaveCustomSettings(pin, type));
      if (pinsWithCustomSettings.length === 0) return null;

      const firstComponent = pins.flatMap((pin) => pin.pinComponents).find((pc) => pc.type === type);
      if (!firstComponent) return null;

      const baseGroup: ComponentTypeGroup = {
        type,
        label: t(`contextSidebar.title.${type}`),
        settingsKeys: getResetablePinComponentSettings(firstComponent),
      };

      if (includeCount) {
        return {
          ...baseGroup,
          affectedPinCount: pinsWithCustomSettings.length,
        };
      }

      return baseGroup;
    })
    .filter((group): group is ComponentTypeGroup | ComponentTypeGroupWithCount => group !== null);
}

export function countPinsWithCustomBackgrounds(pins: Pin[]): number {
  return pins.filter((pin) => pinHasCustomPinSettings(pin)).length;
}

export function getPinsHaveCustomSettings(pins: Pin[]): boolean {
  return pins.some((pin) => pinHasCustomPinSettings(pin));
}
